import React, { StrictMode, Suspense, useEffect } from 'react'
import { Provider } from 'react-redux'
import { Route, Routes } from 'react-router'
import { AnalyticsHeartbeatService, GlobalStyles, Loader, UpdateBanner } from './components/shared'
import ConnectedViewsContainer from './ConnectedViewsContainer'
import SnackbarWrapper from './containers/SnackbarWrapper'
import { GlobalErrorBoundary } from './errors/GlobalErrorBoundary'
import { developmentMode } from './misc/environment.utilities'
import appStore from './store'
import { DndProvider } from 'react-dnd-multi-backend'
import { HTML5toTouch } from 'rdndmb-html5-to-touch'
// Styles
import './styles/main.scss'
import '@follow/elfe/dist/esm/index.css'
import { theme } from './styles/legacy/styled/styled'
import { LogoutPage } from './pages/authent'
import { BrowserRouter } from 'react-router-dom'
import { NavigationUtils } from './components/navigation'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ThemeProvider } from '@emotion/react'
import { queryClient } from './queryClient'
import { UiThemeProvider } from '@follow-health/fw_ui_library/dist/core/provider/ui.theme.provider'
import { PrintingProvider } from './core/provider/printing-context.provider'
import { PrintablePdfProvider } from './core/provider/printable-pdf-context.provider'
import { DownloadingProvider } from './core/provider/downloading-context.provider'
import { DictationProvider } from './core/provider/dictation-context.provider'

// Cool feature, webpack output if(true) or if(false)
if (developmentMode) {
  console.warn('Dev mode enabled')
  // Redirection vers le basename en dev mode
  if (window.location.pathname === '/') {
    window.location.assign(process.env.PUBLIC_URL)
  }
}

const ReactQueryDevtoolsProduction = React.lazy(() =>
  import(
    /*  webpackChunkName: "react-query-devtools" */ '@tanstack/react-query-devtools/build/modern/production.js'
  ).then((d) => ({
    default: d.ReactQueryDevtools,
  })),
)

const App = () => {
  const [showDevtools, setShowDevtools] = React.useState(false)

  useEffect(() => {
    window.toggleDevtools = () => setShowDevtools((old) => !old)
  }, [])

  return (
    <UiThemeProvider>
      <StrictMode>
        <GlobalStyles />
        <GlobalErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <ReactQueryDevtools buttonPosition="top-left" />
            {showDevtools && (
              <Suspense fallback={<Loader />}>
                <ReactQueryDevtoolsProduction />
              </Suspense>
            )}
            <UpdateBanner />
            <BrowserRouter basename={process.env.PUBLIC_URL}>
              <NavigationUtils />
              <Provider store={appStore}>
                <DictationProvider>
                  <ThemeProvider theme={theme}>
                    <DndProvider options={HTML5toTouch}>
                      <DownloadingProvider>
                        <PrintingProvider>
                          <PrintablePdfProvider>
                            <Routes>
                              <Route path="/logout" element={<LogoutPage />} />
                              <Route path="*" element={<ConnectedViewsContainer />} />
                            </Routes>
                            <SnackbarWrapper />
                            {!developmentMode && <AnalyticsHeartbeatService />}
                            <div id="bottom-panel-root" />
                            <div id="modal-root" />
                          </PrintablePdfProvider>
                        </PrintingProvider>
                      </DownloadingProvider>
                    </DndProvider>
                  </ThemeProvider>
                </DictationProvider>
              </Provider>
            </BrowserRouter>
          </QueryClientProvider>
        </GlobalErrorBoundary>
      </StrictMode>{' '}
    </UiThemeProvider>
  )
}
export default App
